export type EasterWallpaperCategorySlug =
  | "iphone"
  | "desktop-4k"
  | "cute"
  | "christian";

export type EasterWallpaperAsset = {
  id: string;
  category: EasterWallpaperCategorySlug;
  src: string;
  alt: string;
  downloadName: string;
  width: number;
  height: number;
  note: string;
};

export type EasterWallpaperCategory = {
  slug: EasterWallpaperCategorySlug;
  navTitle: string;
  badge: string;
  title: string;
  description: string;
  introTitle: string;
  introBody: string[];
  heroNote: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string[];
  collectionLabel: string;
  tips: string[];
  wallpapers: EasterWallpaperAsset[];
  faq: Array<{
    question: string;
    answer: string;
  }>;
};

const buildWallpaper = (
  category: EasterWallpaperCategorySlug,
  name: string,
  width: number,
  height: number,
  note: string,
  index: number
): EasterWallpaperAsset => ({
  id: `${category}-${index}`,
  category,
  src: `/images/easter-wallpapers/${category}/${name}.png`,
  alt: `${note} Easter wallpaper`,
  downloadName: `${name}.png`,
  width,
  height,
  note,
});

const iphoneWallpapers = [
  buildWallpaper(
    "iphone",
    "easter-wallpaper-iphone-1",
    1290,
    2796,
    "Pastel floral eggs for iPhone lock screens",
    1
  ),
  buildWallpaper(
    "iphone",
    "easter-wallpaper-iphone-2",
    1290,
    2796,
    "Soft mint and blush Easter lock screen",
    2
  ),
  buildWallpaper(
    "iphone",
    "easter-wallpaper-iphone-3",
    1290,
    2796,
    "Lilac sunrise Easter iPhone wallpaper",
    3
  ),
  buildWallpaper(
    "iphone",
    "easter-wallpaper-iphone-4",
    1290,
    2796,
    "Blue spring garden Easter phone wallpaper",
    4
  ),
];

const desktopWallpapers = [
  buildWallpaper(
    "desktop-4k",
    "easter-wallpaper-desktop-4k-1",
    1920,
    1080,
    "Wide Easter meadow desktop wallpaper",
    1
  ),
  buildWallpaper(
    "desktop-4k",
    "easter-wallpaper-desktop-4k-2",
    1920,
    1080,
    "Cloudy pastel Easter desktop background",
    2
  ),
  buildWallpaper(
    "desktop-4k",
    "easter-wallpaper-desktop-4k-3",
    1920,
    1080,
    "Lavender Easter 4K desktop scene",
    3
  ),
  buildWallpaper(
    "desktop-4k",
    "easter-wallpaper-desktop-4k-4",
    1920,
    1080,
    "Fresh green Easter sunrise desktop wallpaper",
    4
  ),
];

const cuteWallpapers = [
  buildWallpaper(
    "cute",
    "easter-wallpaper-cute-1",
    1600,
    2000,
    "Cute bunny Easter wallpaper with soft flowers",
    1
  ),
  buildWallpaper(
    "cute",
    "easter-wallpaper-cute-2",
    1600,
    2000,
    "Sweet bunny trio Easter wallpaper",
    2
  ),
  buildWallpaper(
    "cute",
    "easter-wallpaper-cute-3",
    1600,
    2000,
    "Candy-pastel Easter wallpaper with blossoms",
    3
  ),
  buildWallpaper(
    "cute",
    "easter-wallpaper-cute-4",
    1600,
    2000,
    "Playful bunny and egg Easter background",
    4
  ),
];

const christianWallpapers = [
  buildWallpaper(
    "christian",
    "easter-wallpaper-christian-1",
    1600,
    2000,
    "Christian Easter wallpaper with sunrise cross",
    1
  ),
  buildWallpaper(
    "christian",
    "easter-wallpaper-christian-2",
    1600,
    2000,
    "Resurrection morning Easter wallpaper",
    2
  ),
  buildWallpaper(
    "christian",
    "easter-wallpaper-christian-3",
    1600,
    2000,
    "Soft gold Christian Easter phone wallpaper",
    3
  ),
  buildWallpaper(
    "christian",
    "easter-wallpaper-christian-4",
    1600,
    2000,
    "Minimal Christian Easter floral cross wallpaper",
    4
  ),
];

export const easterWallpaperCategories: EasterWallpaperCategory[] = [
  {
    slug: "iphone",
    navTitle: "iPhone",
    badge: "Lock Screens",
    title: "Easter iPhone Wallpapers",
    description:
      "Tall Easter wallpapers tuned for iPhone lock screens, mixing soft florals, polished eggs, and calm spring light.",
    introTitle: "Pastel lock screens that still feel crisp",
    introBody: [
      "This collection leans into elegant vertical composition: fewer distractions, more breathing room, and enough detail to feel special every time you wake the screen.",
      "The artwork is original to the site, so you get Easter imagery that feels curated instead of clipped from crowded stock pages.",
    ],
    heroNote: "Portrait-first originals for lock-screen use",
    seoTitle: "Easter iPhone Wallpapers",
    seoDescription:
      "Download original Easter iPhone wallpapers with pastel eggs, spring florals, and soft sunrise color built for lock screens.",
    seoKeywords: [
      "easter iphone wallpapers",
      "easter lock screen",
      "cute easter iphone wallpaper",
      "pastel easter wallpaper",
    ],
    collectionLabel: "4 original lock-screen wallpapers",
    tips: [
      "Use these for lock screens and home-screen pairings.",
      "Portrait crops stay clean around time widgets and notifications.",
      "Best fit for spring, pastel, and cute Easter searches.",
    ],
    wallpapers: iphoneWallpapers,
    faq: [
      {
        question: "Are these wallpapers made for lock screens?",
        answer:
          "Yes. The iPhone set uses vertical compositions with calmer center space so clocks and widgets do not fight the artwork.",
      },
      {
        question: "Can I use them on Android too?",
        answer:
          "Yes. They are optimized for tall phones first, but most Android lock screens will crop them well.",
      },
    ],
  },
  {
    slug: "desktop-4k",
    navTitle: "Desktop 4K",
    badge: "Wide Scenes",
    title: "Easter Desktop 4K Wallpapers",
    description:
      "Wide Easter backgrounds for laptops and large displays, built around meadow horizons, sunrise glows, and clean negative space.",
    introTitle: "4K-style Easter scenes for larger screens",
    introBody: [
      "The desktop collection is intentionally wider and calmer, so icons can sit on top without destroying the composition.",
      "Instead of noisy holiday collages, these pieces use sky, meadow, and repeating egg motifs to create a stronger desktop mood.",
    ],
    heroNote: "Landscape compositions for monitors and laptops",
    seoTitle: "Easter Desktop 4K Wallpapers",
    seoDescription:
      "Download Easter desktop 4K wallpapers with sunrise meadows, pastel eggs, and clean landscape compositions for large screens.",
    seoKeywords: [
      "easter desktop wallpaper",
      "easter 4k wallpaper",
      "easter wallpaper desktop",
      "spring desktop wallpaper",
    ],
    collectionLabel: "4 original wide wallpapers",
    tips: [
      "Best on laptops, iMacs, and wider external monitors.",
      "Icons and menu bars stay readable because the horizon line sits low.",
      "Use these for desktop, 4K, and spring workspace searches.",
    ],
    wallpapers: desktopWallpapers,
    faq: [
      {
        question: "Are these true 4K files?",
        answer:
          "They are designed in a 16:9 wide format and work well on large displays; you can scale them cleanly for typical desktop use.",
      },
      {
        question: "Will they work on ultrawide displays?",
        answer:
          "They can, but you may want to center-crop them depending on your monitor ratio.",
      },
    ],
  },
  {
    slug: "cute",
    navTitle: "Cute",
    badge: "Bunnies",
    title: "Cute Easter Wallpapers",
    description:
      "Playful Easter wallpapers with bunnies, rounded eggs, and candy-soft palettes that stay polished instead of cartoon-chaotic.",
    introTitle: "Cute without becoming cluttered",
    introBody: [
      "These wallpapers lean into friendly silhouettes and warmer palettes, but the spacing stays controlled so the result still feels designed.",
      "They are good for users who want obvious Easter energy while keeping the artwork a little more refined than novelty graphics.",
    ],
    heroNote: "Bunny-led art direction with softer spring color",
    seoTitle: "Cute Easter Wallpapers",
    seoDescription:
      "Browse cute Easter wallpapers with sweet bunny silhouettes, pastel eggs, and spring flower details for phones and tablets.",
    seoKeywords: [
      "cute easter wallpaper",
      "bunny easter wallpaper",
      "pastel easter background",
      "sweet easter wallpaper",
    ],
    collectionLabel: "4 playful Easter wallpapers",
    tips: [
      "Great fit for users searching bunny, pastel, and cute wallpaper modifiers.",
      "The artwork is bright but still has enough empty space to function as a wallpaper.",
      "Best for phones, tablets, and portrait-friendly crops.",
    ],
    wallpapers: cuteWallpapers,
    faq: [
      {
        question: "Do the cute wallpapers still work behind icons?",
        answer:
          "Yes. Even the playful pieces keep the densest details low or off-center so app icons remain readable.",
      },
      {
        question: "Are these only for kids?",
        answer:
          "No. The tone is playful, but the compositions stay editorial and tidy rather than juvenile.",
      },
    ],
  },
  {
    slug: "christian",
    navTitle: "Christian",
    badge: "Resurrection Morning",
    title: "Christian Easter Wallpapers",
    description:
      "Christian Easter wallpapers centered on sunrise, crosses, lilies, and a quieter resurrection mood.",
    introTitle: "A reverent Easter collection with softer symbolism",
    introBody: [
      "The Christian set avoids loud sermon graphics and instead uses sunrise light, floral framing, and cross silhouettes to keep the feeling contemplative.",
      "It is designed for people searching specifically for resurrection or faith-centered Easter backgrounds without sacrificing visual quality.",
    ],
    heroNote: "Quiet Easter imagery focused on resurrection themes",
    seoTitle: "Christian Easter Wallpapers",
    seoDescription:
      "Download Christian Easter wallpapers with sunrise crosses, floral details, and calm resurrection-inspired compositions.",
    seoKeywords: [
      "christian easter wallpaper",
      "religious easter wallpaper",
      "resurrection wallpaper",
      "cross easter background",
    ],
    collectionLabel: "4 faith-centered Easter wallpapers",
    tips: [
      "Designed around Christian Easter search intent.",
      "Good for phones, tablets, and devotional device themes.",
      "More restrained and contemplative than novelty Easter graphics.",
    ],
    wallpapers: christianWallpapers,
    faq: [
      {
        question: "Are these wallpapers explicitly Christian?",
        answer:
          "Yes. They focus on cross and resurrection themes while keeping the visual language calm and polished.",
      },
      {
        question: "Do they still feel modern?",
        answer:
          "Yes. The goal is faith-centered imagery with cleaner design, not dated clip art or heavy text overlays.",
      },
    ],
  },
];

export const easterNavigationItems = [
  { title: "Collections", href: "/#collections" },
  { title: "iPhone", href: "/wallpapers/iphone" },
  { title: "Desktop 4K", href: "/wallpapers/desktop-4k" },
  { title: "Cute", href: "/wallpapers/cute" },
  { title: "Christian", href: "/wallpapers/christian" },
  { title: "Generate", href: "/#generate" },
];

export const easterFooterColumns = [
  {
    title: "Collections",
    items: [
      { title: "Easter iPhone Wallpapers", href: "/wallpapers/iphone" },
      { title: "Easter Desktop 4K Wallpapers", href: "/wallpapers/desktop-4k" },
      { title: "Cute Easter Wallpapers", href: "/wallpapers/cute" },
      { title: "Christian Easter Wallpapers", href: "/wallpapers/christian" },
    ],
  },
  {
    title: "Explore",
    items: [
      { title: "Featured Wallpapers", href: "/#featured" },
      { title: "Generate an Easter Wallpaper", href: "/#generate" },
      { title: "FAQ", href: "/#faq" },
    ],
  },
  {
    title: "Legal",
    items: [
      { title: "Privacy Policy", href: "/privacy-policy" },
      { title: "Terms of Service", href: "/terms-of-service" },
      { title: "Refund Policy", href: "/refund-policy" },
    ],
  },
];

export const easterHomeFeatures = [
  {
    title: "Original Easter Artwork",
    description:
      "Every wallpaper on the site is created specifically for this collection instead of scraped from generic image boards.",
  },
  {
    title: "Collections by Intent",
    description:
      "The public IA follows the actual search modifiers people use: iPhone, desktop 4K, cute, and Christian.",
  },
  {
    title: "Direct Download Flow",
    description:
      "Every wallpaper card exposes a clean same-origin download path so users do not fight broken third-party download buttons.",
  },
  {
    title: "Built-In Generator",
    description:
      "If the curated set is not enough, the homepage includes a lightweight generator for custom Easter wallpaper ideas.",
  },
];

export const easterGeneratorPrompts = [
  "pastel easter eggs in a meadow at sunrise",
  "cute bunny easter wallpaper with soft pink flowers",
  "christian easter cross on a glowing morning horizon",
];

export const easterHomeFaq = [
  {
    question: "Are these Easter wallpapers free to download?",
    answer:
      "Yes. The featured collections on the site are presented as direct downloads with simple file naming and clean wallpaper crops.",
  },
  {
    question: "What kinds of Easter wallpaper pages are on the site?",
    answer:
      "The MVP launch focuses on iPhone, desktop 4K, cute, and Christian Easter collections, because those are the strongest search-intent modifiers for the main keyword.",
  },
  {
    question: "Do I need an account to download the curated wallpapers?",
    answer:
      "No. The curated wallpapers are surfaced as direct same-origin downloads from the public site.",
  },
  {
    question: "What does the generator do?",
    answer:
      "The generator takes your prompt and wraps it in Easter-specific art direction so you can quickly make a custom spring wallpaper variation.",
  },
];

export const easterFeaturedWallpapers = [
  iphoneWallpapers[0],
  desktopWallpapers[0],
  cuteWallpapers[0],
  christianWallpapers[0],
  iphoneWallpapers[1],
  desktopWallpapers[1],
  cuteWallpapers[1],
  christianWallpapers[1],
];

export const easterSiteStats = [
  { value: "16", label: "original wallpapers at launch" },
  { value: "4", label: "search-intent collections" },
  { value: "1", label: "custom generator built in" },
];

export const getEasterWallpaperCategory = (slug: string) =>
  easterWallpaperCategories.find((category) => category.slug === slug);
